// Sensor classes (Massimo 2026-10-25): the tenant's own label cold_class.
// Names per language from the label definition, {regime, target} from the
// label values, limits from the class's live rules (data/rules.ts). The
// defaults below are only the fallback before the first load.
import { signal } from "@preact/signals";
import { tenantApi } from "../lib/account";

export interface Cls { target: number; warn: number; alarm: number; crit: number; low: number | null; range: string; regime: string; names: Record<string, string> }

const d = (regime: string, target: number, warn: number, alarm: number, crit: number, low: number | null, en: string): Cls => ({ regime, target, warn, alarm, crit, low, range: "", names: { en } });
export const REGIMES: Record<string, Cls> = {
  frozen: d("frozen", -19, -16, -15, -12, null, "Frozen"),
  chilled: d("chilled", 2.8, 4.5, 7, 9, -0.5, "Chilled"),
  cool: d("cool", 12, 16.5, 18, 20, 6, "Cool"),
};
/** bumped when the classes change, so pickers re-render */
export const classesVersion = signal(0);

const fmt = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v)}`;
export function rangeOf(c: Cls) { return c.low != null ? `${fmt(c.low)}…${fmt(c.warn)} °C` : `≤ ${fmt(c.warn)} °C`; }

/** in range = not above the alarm limit and not below the low limit */
export const inRange = (cls: string, v: number) => {
  const r = REGIMES[cls];
  return !r || (v <= r.alarm && (r.low == null || v >= r.low));
};

export async function loadClasses() {
  const [def, vals] = await Promise.all([
    tenantApi<{ enum_values?: string[]; texts?: Record<string, Record<string, { text: string }>> }>("GET", "/property-defs/cold_class?texts=all"),
    tenantApi<{ items: { value: string; display_name: string | null; data?: { regime?: string; target?: number } }[] }>("GET", "/labels/cold_class/values"),
  ]);
  const data = new Map(vals.items.map((x) => [x.value, x]));
  for (const k of Object.keys(REGIMES)) if (!(def.enum_values || []).includes(k)) delete REGIMES[k];
  for (const k of def.enum_values || []) {
    const v = data.get(k);
    const names: Record<string, string> = {};
    for (const [lang, tx] of Object.entries(def.texts || {})) if (tx[`value.${k}`]?.text) names[lang] = tx[`value.${k}`].text;
    if (!names.en) names.en = v?.display_name || k;
    const cur = REGIMES[k] || d(v?.data?.regime || k, v?.data?.target ?? 0, 0, 0, 0, null, names.en);
    REGIMES[k] = { ...cur, names, regime: v?.data?.regime || cur.regime, target: v?.data?.target ?? cur.target };
  }
  for (const c of Object.values(REGIMES)) c.range = rangeOf(c);
  classesVersion.value++;
}
export const classKeys = () => Object.keys(REGIMES);
/** a class's name in the language (falls back to English, then the key) */
export const clsName = (lang: string, key: string) => (REGIMES[key]?.names[lang] || REGIMES[key]?.names.en || key) + (REGIMES[key]?.range ? ` (${REGIMES[key].range})` : "");
